/**
 * Resolve buyer / address fields for sales order resolve payload (create + edit).
 */
export type PickSalesResolveBuyerInput = {
  values: {
    userType?: string;
    isSameAsBilling?: boolean;
    userFirstName?: string;
    userLastName?: string;
    userEmail?: string;
    userPhone?: string;
    teamMemberId?: string;
    teamMemberName?: string;
  };
  selectedOfficeAgent?: { value?: string; firstName?: string; lastName?: string; label?: string } | null;
  selectedAgent?: {
    value?: string;
    firstName?: string;
    lastName?: string;
    label?: string;
    brokerageFirmName?: string;
  } | null;
  selectedOffice?: { value?: string; label?: string } | null;
  offices?: Array<{ _id?: string; officeAddress?: unknown[] }> | null;
  selectedBrokerageFirm?: { value?: string; label?: string } | null;
  /** Fallback when teamMemberId is not yet in form values (matches buildSalesOrder payload builder). */
  selectedTeamMemberOption?: { value?: string } | null;
  selectedBillingAddress?: unknown;
  selectedShippingAddress?: unknown;
  selectedOfficeBillingAddress?: unknown;
  selectedOfficeShippingAddress?: unknown;
};

export type SalesResolveBuyerContext = {
  customerId: string;
  agentId: string | null;
  agentName: string | null;
  brokerageFirmId: string | null;
  brokerageFirmName: string | null;
  office: unknown;
  inventorySource: string;
  billingAddress: unknown;
  shippingAddress: unknown;
  buyerType: string;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhone: string;
  teamMemberId: string | null;
  teamMemberName: string;
};

export function pickSalesResolveBuyerContext(
  input: PickSalesResolveBuyerInput
): SalesResolveBuyerContext {
  const { values } = input;
  let customerId = "";
  let agentId: string | null = null;
  let agentName: string | null = null;
  let brokerageFirmId: string | null = null;
  let office: unknown = null;
  let inventorySource = "agent";
  let billingAddress = input.selectedBillingAddress;
  let shippingAddress =
    values.isSameAsBilling && input.selectedBillingAddress
      ? billingAddress
      : input.selectedShippingAddress;

  if (input.selectedOfficeAgent) {
    agentId = input.selectedOfficeAgent.value ?? null;
    const first = input.selectedOfficeAgent.firstName ?? "";
    const last = input.selectedOfficeAgent.lastName ?? "";
    agentName = (input.selectedAgent?.label ?? `${first} ${last}`).trim();
    customerId = String(input.selectedOfficeAgent.value ?? "");
    brokerageFirmId = input.selectedBrokerageFirm?.value ?? null;
  } else if (input.selectedAgent) {
    agentId = input.selectedAgent.value ?? null;
    const first = input.selectedAgent.firstName ?? "";
    const last = input.selectedAgent.lastName ?? "";
    agentName = (input.selectedAgent.label ?? `${first} ${last}`).trim();
    customerId = String(input.selectedAgent.value ?? "");
  } else if (input.selectedOffice) {
    const selectedOfficeData = input.offices?.find((o) => o._id === input.selectedOffice?.value);
    const primaryAddress = selectedOfficeData?.officeAddress?.[0];
    office = {
      _id: input.selectedOffice.value,
      officeName: input.selectedOffice.label,
      officeType: input.selectedOffice.label,
      officeAddress: primaryAddress,
      status: true,
    };
    customerId = String(input.selectedBrokerageFirm?.value ?? "");
    brokerageFirmId = input.selectedBrokerageFirm?.value ?? null;
    inventorySource = "office";
    agentId = null;
    billingAddress = input.selectedOfficeBillingAddress || input.selectedBillingAddress;
    shippingAddress =
      values.isSameAsBilling && billingAddress
        ? billingAddress
        : (input.selectedOfficeShippingAddress || input.selectedShippingAddress);
  }

  // Match buildSalesOrderPayloadForApi customer_id priority:
  // office agent → agent → team member → brokerage firm fallback
  const effectiveTeamMemberId = String(
    values.teamMemberId ?? input.selectedTeamMemberOption?.value ?? ""
  ).trim();
  if (input.selectedOfficeAgent?.value) {
    customerId = String(input.selectedOfficeAgent.value);
  } else if (input.selectedAgent?.value) {
    customerId = String(input.selectedAgent.value);
  } else if (effectiveTeamMemberId) {
    customerId = effectiveTeamMemberId;
  } else if (!customerId && input.selectedBrokerageFirm?.value) {
    customerId = String(input.selectedBrokerageFirm.value);
  }

  return {
    customerId,
    agentId,
    agentName,
    brokerageFirmId,
    brokerageFirmName:
      input.selectedBrokerageFirm?.label ??
      input.selectedAgent?.brokerageFirmName ??
      null,
    office,
    inventorySource,
    billingAddress,
    shippingAddress,
    buyerType: String(values.userType ?? ""),
    customerFirstName: String(values.userFirstName ?? ""),
    customerLastName: String(values.userLastName ?? ""),
    customerEmail: String(values.userEmail ?? ""),
    customerPhone: String(values.userPhone ?? ""),
    teamMemberId: values.teamMemberId ? String(values.teamMemberId) : null,
    teamMemberName: String(values.teamMemberName ?? ""),
  };
}
